// Design Ref: §5 Portfolio.js — overview 탭 메인 레이아웃
// Plan SC: FR-03 (4 차트 + 필터), FR-04 (빈 데이터 placeholder), FR-06 (모바일 반응형)
import React, { useMemo, useState } from 'react';
import {
  monthlyPnL,
  cumulativeReturn,
  winRateByMonth,
  sectorDistribution,
  topWinnersLosers,
  overallStats,
} from './portfolioUtils';
import BarChart from './charts/BarChart';
import LineChart from './charts/LineChart';
import DonutChart from './charts/DonutChart';
import WinRateBar from './charts/WinRateBar';

const PERIODS = [
  { key: '1M', label: '이번 달' },
  { key: '3M', label: '최근 3M' },
  { key: 'YTD', label: 'YTD' },
  { key: '1Y', label: '최근 1Y' },
];

/**
 * Portfolio overview 탭
 * Props:
 *  - stocks: enriched trades (App.js krEnriched / usEnriched)
 *  - market: 'KR' | 'US'
 *  - fmt: (n) => string (optional 통화 포맷터)
 */
export default function Portfolio({ stocks = [], market = 'KR', fmt }) {
  const [period, setPeriod] = useState('YTD');
  const useKrw = market === 'US';
  const formatMoney = fmt || ((n) => (n >= 0 ? '+' : '') + Math.round(n).toLocaleString() + (useKrw ? '원' : '원'));

  const stats = useMemo(() => overallStats(stocks, { useKrw }), [stocks, useKrw]);
  const monthly = useMemo(() => monthlyPnL(stocks, period, { useKrw }), [stocks, period, useKrw]);
  const cumulative = useMemo(() => cumulativeReturn(stocks, period, { useKrw }), [stocks, period, useKrw]);
  const winRate = useMemo(() => winRateByMonth(stocks, period), [stocks, period]);
  const sectors = useMemo(() => sectorDistribution(stocks, { useKrw, fallbackLabel: market === 'KR' ? 'KOSPI/KOSDAQ' : '해외' }), [stocks, useKrw, market]);
  const { winners, losers } = useMemo(() => topWinnersLosers(stocks, 5), [stocks]);

  const monthlyData = monthly.map(m => ({ label: m.month, value: m.value }));
  const cumulativeData = cumulative.map(m => ({ label: m.month, value: m.pct }));

  const hasAnyData = stocks.length > 0;
  if (!hasAnyData) {
    return <EmptyState market={market} />;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Header + 필터 */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
        <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 22, color: '#f0c040' }}>
          {market === 'KR' ? '국내' : '해외'} Portfolio Overview
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          {PERIODS.map(p => (
            <button
              key={p.key}
              onClick={() => setPeriod(p.key)}
              style={{
                padding: '6px 14px',
                border: '1px solid var(--border-light)',
                background: period === p.key ? '#3C50E0' : 'var(--bg-card)',
                color: period === p.key ? '#fff' : 'var(--text-sub)',
                borderRadius: 6,
                fontSize: 11,
                fontFamily: "'DM Mono', monospace",
                cursor: 'pointer',
                letterSpacing: '0.05em',
              }}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* Stat 카드 4개 */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12 }}>
        <StatCard label="매도 거래수" value={`${stats.totalTrades}건`} />
        <StatCard label="전체 승률" value={stats.winRate !== null ? stats.winRate + '%' : '—'} color={stats.winRate >= 50 ? '#34d399' : '#fb7185'} />
        <StatCard label="총 실현손익" value={formatMoney(stats.totalPnl)} color={stats.totalPnl >= 0 ? '#f04452' : '#3182f6'} />
        <StatCard label="평균 수익률" value={stats.avgReturn !== null ? (stats.avgReturn >= 0 ? '+' : '') + stats.avgReturn + '%' : '—'} color={stats.avgReturn >= 0 ? '#f04452' : '#3182f6'} />
      </div>

      {/* 차트 2x2 그리드 */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 12 }}>
        <BarChart title="월별 실현 P&L" data={monthlyData} formatValue={formatMoney} />
        <LineChart title="누적 수익률" data={cumulativeData} color="#f0c040" />
        <WinRateBar title="월별 승률" data={winRate} />
        <DonutChart title="섹터/시장 분포 (보유)" data={sectors} formatValue={formatMoney} />
      </div>

      {/* Top 승/패 */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 12 }}>
        <TopList title="Top 5 승 종목" items={winners} positive useKrw={useKrw} formatMoney={formatMoney} />
        <TopList title="Top 5 패 종목" items={losers} positive={false} useKrw={useKrw} formatMoney={formatMoney} />
      </div>
    </div>
  );
}

function StatCard({ label, value, color = 'var(--text)' }) {
  return (
    <div style={{ background: 'var(--bg-table)', border: '1px solid var(--border-light)', borderRadius: 12, padding: '14px 16px' }}>
      <div style={{ fontSize: 10, color: 'var(--text-sub)', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 6 }}>{label}</div>
      <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 18, fontWeight: 700, color }}>{value}</div>
    </div>
  );
}

function TopList({ title, items, positive, useKrw, formatMoney }) {
  const color = positive ? '#f04452' : '#3182f6';
  return (
    <div style={{ background: 'var(--bg-table)', border: '1px solid var(--border-light)', borderRadius: 12, padding: '14px 16px' }}>
      <div style={{ fontSize: 12, color: 'var(--text-sub)', letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: 10 }}>{title}</div>
      {items.length === 0 ? (
        <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-muted)', fontSize: 12 }}>
          {positive ? '수익 거래 없음' : '손실 거래 없음'}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {items.map((t, i) => {
            const p = Number(useKrw ? (t.profitKrw ?? t.profit ?? 0) : (t.profit ?? t.profitKrw ?? 0));
            const rate = t.profitRate;
            return (
              <div key={(t.name || '') + i} style={{ display: 'grid', gridTemplateColumns: '20px 1fr auto auto', gap: 8, alignItems: 'center', fontSize: 12 }}>
                <div style={{ fontFamily: "'DM Mono', monospace", color: 'var(--text-sub)', fontSize: 10 }}>{i + 1}.</div>
                <div style={{ fontWeight: 600, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.name}</div>
                <div style={{ fontFamily: "'DM Mono', monospace", color, fontSize: 12, fontWeight: 700 }}>
                  {formatMoney(p)}
                </div>
                <div style={{ fontFamily: "'DM Mono', monospace", color, fontSize: 10 }}>
                  {rate != null ? (rate >= 0 ? '+' : '') + rate.toFixed(1) + '%' : ''}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function EmptyState({ market }) {
  return (
    <div style={{
      background: 'var(--bg-table)',
      border: '1px dashed var(--border-light)',
      borderRadius: 16,
      padding: 60,
      textAlign: 'center',
      color: 'var(--text-muted)',
    }}>
      <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 22, color: '#f0c040', marginBottom: 12 }}>
        Portfolio Overview
      </div>
      <div style={{ fontSize: 13, color: 'var(--text-sub)', lineHeight: 1.6 }}>
        아직 {market === 'KR' ? '국내' : '해외'} 매매 기록이 없습니다.
        <br />
        <span style={{ fontSize: 12 }}>매매일지 탭에서 매매 기록을 추가하면 자동으로 성과가 시각화됩니다.</span>
      </div>
    </div>
  );
}
